import React, { useEffect, useState, useCallback } from "react";
import { Box, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { READ_ALL_COMPANIES } from "../../graph/companies/queries";
import { ARCHIVE_COMPANY } from "../../graph/companies/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const formatEnumLabel = (value) => {
  if (!value) return "—";

  return value
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const ManageCompanies = () => {
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();

  const [companies, setCompanies] = useState([]);

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await client.request(READ_ALL_COMPANIES);

      const formattedCompanies = (data.readAllCompanies || []).map((company) => ({
        ...company,
        id: company.id,
        displayName: company.tradingName || company.legalName,
        industryLabel: formatEnumLabel(company.industry),
        statusLabel: formatEnumLabel(company.status),
        companySizeBandLabel: formatEnumLabel(company.companySizeBand),
      }));

      setCompanies(formattedCompanies);
    } catch (err) {
      console.error("Failed to fetch companies:", err);
      setCompanies([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load companies",
      });
    }
  }, [setSnackbar]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleArchiveCompany = async (company) => {
    if (!company?.id) return;

    try {
      await client.request(ARCHIVE_COMPANY, { id: company.id });
      await fetchCompanies();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Company archived successfully",
      });
    } catch (err) {
      console.error("Failed to archive company:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to archive company",
      });
    }
  };

  const columns = [
    { field: "displayName", headerName: "Company", flex: 1.4 },
    { field: "legalName", headerName: "Legal Name", flex: 1.3 },
    {
      field: "industryLabel",
      headerName: "Industry",
      flex: 0.9,
      renderCell: ({ value }) => (
        <Chip label={value || "—"} size="small" variant="outlined" />
      ),
    },
    {
      field: "statusLabel",
      headerName: "Status",
      flex: 0.9,
      renderCell: ({ value }) => (
        <Chip label={value || "—"} size="small" variant="outlined" />
      ),
    },
    {
      field: "companySizeBandLabel",
      headerName: "Size",
      flex: 0.8,
    },
    { field: "email", headerName: "Email", flex: 1.1 },
    { field: "phone", headerName: "Phone", flex: 0.9 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onView={() => navigate(`/companies/${row.id}`)}
          onEdit={() =>
            navigate(`/companies/edit/${row.id}`, {
              state: { redirectPath: "/manageCompanies" },
            })
          }
          onDelete={() => handleArchiveCompany(row)}
          hideCreate
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="COMPANIES"
          subtitle="Manage your company database and target market"
        />

        <ActionButton
          tone="gold"
          onClick={() =>
            navigate("/createCompany", {
              state: { redirectPath: "/manageCompanies" },
            })
          }
        >
          Create Company
        </ActionButton>
      </Box>

      <ThemedDataGrid rows={companies} columns={columns} />
    </Box>
  );
};

export default ManageCompanies;